import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AdminTable } from "./lib";
import { useSiteConfig } from "@/lib/siteConfig";

const GRID = "grid-cols-[minmax(200px,1fr)_130px_85px_85px_85px_70px_95px_70px]";
// Built per render rather than as a constant: the GST heading has to say when the rate under it is
// not being charged, and a header reading plain "GST %" over a column of greyed inputs is the
// ambiguity this whole change is about.
const columnsFor = (gstOn) => [
  { key: "name", label: "Document" },
  { key: "group", label: "Group" },
  { key: "stamp_paper_cost", label: "Stamp ₹" },
  { key: "service_cost", label: "Service ₹" },
  { key: "delivery_cost", label: "Delivery ₹" },
  { key: "gst_percent", label: gstOn ? "GST %" : "GST % (off)" },
  { key: "total", label: "Total" },
  { key: "enabled", label: "Live" },
];
const NUMERIC = ["stamp_paper_cost", "service_cost", "delivery_cost", "gst_percent"];

// Orderable services with no template of their own — the customer uploads a document rather than
// drafting one, so there is nothing to template, but they still need a price.
const UPLOAD_SERVICES = ["contract_review", "property_verification"];

/**
 * What a customer is actually charged for this line.
 *
 * `gstOn` is not optional. This used to add `gst_percent` unconditionally while the server applied
 * it only when a GSTIN was set (core.templates_data.gst_enabled / compute_line) — so on an
 * unregistered install this page advertised 590 for a document the customer was billed 500 for.
 * The admin screen and the invoice disagreed about the price, and the screen was the wrong one.
 */
const lineTotal = (v, gstOn) => {
  const sub = Number(v.stamp_paper_cost || 0) + Number(v.service_cost || 0) + Number(v.delivery_cost || 0);
  if (!gstOn) return sub;
  return sub + Math.round(sub * Number(v.gst_percent || 0)) / 100;
};

/**
 * Pricing — and the single place that says what customers can actually buy.
 *
 * Availability lives here rather than in the Template Catalog because the two questions are one
 * question: *what is on sale, and for how much*. The catalogue is about what a document says.
 *
 * Reads `/admin/templates`, not the public `/templates`: the public list filters out disabled
 * templates, so using it would make a template disappear from the very page you would re-enable
 * it on.
 *
 * Prices are per template, with a fallback chain (core.templates_data.compute_line): the
 * template's own rate, else its group's, else the affidavit default. A row marked *inherited* has
 * no rate of its own yet.
 */
export function AdminPricing() {
  // Derived from the invoice template's GSTIN and published on /site-config — one switch, so this
  // page cannot show a tax the checkout will not charge. See routers/public.site_config.
  const gstOn = Boolean(useSiteConfig()?.gst_enabled);
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  // Nothing is written until Save is pressed — say so, or an edit looks like it took effect.
  const [dirty, setDirty] = useState(false);

  const loadTemplates = () => api.get("/admin/templates").then(r => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/pricing").then(r => setCfg(r.data)).catch(() => {});
    api.get("/template-groups").then(r => setGroups(r.data)).catch(() => {});
    loadTemplates();
  }, []);

  if (!cfg) return <div className="text-[#6B7280]">Loading…</div>;
  const groupLabel = (k) => groups.find(g => g.key === k)?.label || k;

  const rows = [
    ...templates.map(t => ({
      key: t.key, title: t.title, group: t.group || "affidavit",
      enabled: t.enabled !== false, isTemplate: true,
    })),
    ...UPLOAD_SERVICES.map(k => ({
      key: k, title: groupLabel(k), group: k, enabled: true, isTemplate: false,
    })),
  ].map(r => {
    const own = cfg[r.key];
    const v = own || cfg[r.group] || cfg.affidavit || {};
    return { ...r, values: { ...v }, overridden: !!own };
  });

  const visible = groupFilter === "all" ? rows : rows.filter(r => r.group === groupFilter);

  const upd = (row, field, value) => {
    setDirty(true);
    setCfg(c => {
      // Editing an inherited row promotes it to its own entry — otherwise typing a price for one
      // affidavit would silently reprice every affidavit that shares the group rate.
      const base = c[row.key] || c[row.group] || c.affidavit || {};
      // Stored as typed, not Number()-ed. Coercing on every keystroke meant clearing the box gave
      // `Number("") === 0`, so the field snapped back to 0 and you ended up typing around it —
      // which reads as the value changing by itself. Numbers are parsed where they are used
      // (lineTotal) and on save.
      return { ...c, [row.key]: { ...base, [field]: value } };
    });
  };

  const toggleEnabled = async (row, on) => {
    try {
      await api.put(`/admin/templates/${row.key}`, { enabled: on });
      toast.success(on ? `${row.title} is on sale` : `${row.title} hidden from customers`);
      loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(cfg).map(([k, v]) => [k, {
        stamp_paper_cost: Number(v.stamp_paper_cost || 0),
        service_cost: Number(v.service_cost || 0),
        delivery_cost: Number(v.delivery_cost || 0),
        gst_percent: Number(v.gst_percent || 0),
      }]));
      await api.put("/admin/pricing", clean);
      toast.success("Pricing updated");
      setCfg((await api.get("/pricing")).data);
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pricing-page">
      <h1 className="font-serif-display text-3xl md:text-4xl text-white">Pricing &amp; Availability</h1>

      {/* Stated on the page, not left to be inferred from a greyed column. GST being off is a
          decision with a price attached, and the way it used to be discovered was by noticing that
          a customer had been charged less than this page said. */}
      {!gstOn && (
        <div data-testid="pricing-gst-off"
             className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5 text-xs text-[#A0AABF]">
          <b className="text-white">GST is off</b> — no GSTIN is set, so the totals below are what
          customers pay and no tax is charged or shown anywhere. The per-document rates are kept and
          apply the moment you register: turn GST on in{" "}
          <a href="/admin/settings" className="text-[#CBA96C] hover:underline">Settings — Invoice template</a>.
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap" data-testid="pricing-group-tabs">
        {[{ key: "all", label: "All" }, ...groups].map(g => {
          const n = g.key === "all" ? rows.length : rows.filter(r => r.group === g.key).length;
          return (
            <button key={g.key} data-testid={`pricing-group-${g.key}`} onClick={() => setGroupFilter(g.key)}
              className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${
                groupFilter === g.key
                  ? "bg-[#CBA96C] text-[#080C16] border-[#CBA96C] font-medium"
                  : "bg-transparent text-[#A0AABF] border-white/15 hover:text-white hover:border-white/30"
              }`}>{g.label} <span className="opacity-70">({n})</span></button>
          );
        })}
      </div>

      <AdminTable
        testid="admin-pricing" columns={columnsFor(gstOn)} gridCols={GRID} rows={visible}
        rowKey={(r) => r.key} defaultSort="name" empty="Nothing to price in this group."
        searchPlaceholder="Search document…"
        matches={(r, q) => `${r.title} ${r.key} ${r.group}`.toLowerCase().includes(q)}
        accessors={{
          name: (r) => (r.title || "").toLowerCase(),
          group: (r) => r.group || "",
          total: (r) => lineTotal(r.values, gstOn),
          enabled: (r) => (r.enabled ? 1 : 0),
          ...Object.fromEntries(NUMERIC.map(f => [f, (r) => Number(r.values[f] || 0)])),
        }}
        toolbar={
          <>
            {dirty && <span className="text-xs text-[#F5A524] mr-1">Unsaved changes</span>}
            <Button data-testid="pricing-save" disabled={busy} onClick={save} size="sm"
                    className={dirty
                      ? "bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]"
                      : "bg-transparent border border-white/15 text-[#A0AABF] hover:bg-white/5"}>
              {busy ? "Saving…" : "Save pricing"}
            </Button>
          </>
        }
        renderRow={(r) => (
          <>
            <div className="min-w-0 pt-1.5">
              <div className={`text-sm truncate ${r.enabled ? "text-white" : "text-[#6B7280] line-through"}`}>{r.title}</div>
              <div className="font-mono text-[10px] text-[#6B7280] truncate">
                {r.key}{!r.overridden && <span className="ml-2 italic">inherited</span>}
              </div>
            </div>
            <div className="text-xs text-[#A0AABF] pt-1.5 truncate">{groupLabel(r.group)}</div>
            {NUMERIC.map(f => {
              // GST off: the rate stays visible but disabled, and the column header says why.
              // Hiding it would be tidier and worse — the rates are still stored and still apply
              // the moment you register, and a setting you cannot see is what made this page
              // disagree with the invoice in the first place.
              const off = f === "gst_percent" && !gstOn;
              return (
                <div key={f}>
                  <Input data-testid={`price-${r.key}-${f}`} type="number" value={r.values[f] ?? ""}
                         disabled={off} onChange={e => upd(r, f, e.target.value)}
                         title={off ? "Not charged — no GSTIN set. Turn GST on in Settings to apply this rate." : undefined}
                         className={`h-8 text-xs bg-[#080C16] border-white/10 text-white ${
                           off ? "opacity-40 cursor-not-allowed" : ""}`}/>
                </div>
              );
            })}
            <div className="font-serif-display text-sm text-[#CBA96C] pt-1.5">₹{lineTotal(r.values, gstOn).toFixed(0)}</div>
            <div className="pt-1.5">
              {/* Upload services have no template row to toggle — they are always orderable. */}
              {r.isTemplate ? (
                // Green = on sale, black = not. The border is what keeps the black state visible
                // against the dark row — without it an off switch reads as an empty cell.
                <Switch
                  data-testid={`live-${r.key}`} checked={r.enabled}
                  onCheckedChange={(v) => toggleEnabled(r, v)}
                  title={r.enabled ? "On sale — customers can order this" : "Off sale — hidden from customers"}
                  className="data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981] data-[state=unchecked]:bg-[#0B0F17] data-[state=unchecked]:border-white/25"
                />
              ) : (
                <span className="text-[10px] text-[#6B7280]">always</span>
              )}
            </div>
          </>
        )}
      />
    </div>
  );
}
